/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
import JSG from '@cedalo/jsg-ui';
import { NumberFormatter } from '@cedalo/number-format';
import IconClose from '../public/resources/close.png';
import IconCloseBlack from '../public/resources/closeb.png';
import IconInfo from '../public/resources/info.png';
import IconLoop from '../public/resources/loop.png';
import IconLoopWhite from '../public/resources/loopw.png';
import IconConnections from '../public/lib/res/svg/connectionsb.svg';
import IconConnectionsBlack from '../public/lib/res/svg/connections.svg';
import { MoveSheetCommand, ShowSheetCommand, UpdateCustomInfosCommand } from './helper/commands';
import { getCustomInfos, sheetOrderInfo, visibleInfo } from './helper/custominfos';
import { ButtonActivator, MoveActivator, ResizeActivator, SplitterActivator } from './helper/InteractionActivators';
import { intl } from './helper/IntlGlobalProvider';
import CommandStack from './helper/synchronization/CommandStack';
import AppInteractionHandler from './helper/synchronization/AppInteractionHandler';
import * as Localizer from './languages/localizer';
import SheetParserContext from './SheetParserContext';
import store from './store';
import { json as jsonUtils } from './utils';

const {
	AddTreeItemCommand,
	CompoundCommand,
	DeleteTreeItemCommand,
	UpdateTreeItemCommand,
	TreeItemsNode,
	GraphSettings,
	Graph,
	GraphEditor,
	LayoutNodeActivator,
	SheetGraphItemEventActivator,
	SheetPlotActivator,
	EditTextActivator,
	MarqueeActivator,
	ImageDropActivator,
	PanActivator,
	NotificationCenter,
	Notification,
	PinchActivator,
	ReshapeActivator,
	RotateActivator,
	SheetActivator,
	LinkActivator,
	CaptionActivator,
	SetSelectionCommand,
	SetTreeItemDisabledCommand,
	RemoveSelectionCommand,
	SetSheetCellsCommand,
	SheetCommandFactory,
	// SplitterActivator,
	TooltipActivator,
	TreeActivator,
	TreeItem,
	ViewActivator,
	// LayoutNode,
	CellInfoView,
	Expression
} = JSG;

const updateMessageContainer = (container, { split, width }) => {
	if (split != null) container.getMessageList().setHeight(Number(split));
	if (width != null) container.setWidth(Number(width));
};
const setJSONContent = (path, json) => {
	// path is in upper case!!
	const value = jsonUtils.valueAt(path, json, true);
	const data = typeof value === 'object' ? Object.keys(value).map((k) => [k, {}]).sort() : undefined;
	const info = { data, json };
	NotificationCenter.getInstance().send(new Notification('JSON_CONTENT_SET', info));
};

export default class GraphManager {
	constructor(path) {
		const name = localStorage.getItem('theme');

		JSG.init(path);
		JSG.createThreshhold = 150;
		JSG.setDrawingDisabled(true);
		JSG.imagePool.add(IconInfo, 'info');
		JSG.imagePool.add(name === 'Light' ? IconCloseBlack : IconClose, 'close');
		JSG.imagePool.add(IconLoop, 'loop');
		JSG.imagePool.add(IconLoopWhite, 'loopw');
		JSG.imagePool.add(name === 'Light' ? IconConnectionsBlack : IconConnections, 'connections');
		JSG.SelectionStyle.MARKER_FILL_COLOR = '#90B5EE';
		JSG.SelectionStyle.FILL = true;
		JSG.appLocalizer = Localizer;
		JSG.setLocale('en');

		const xhr = JSG._createRequest(`maps/mapinfo.json`, {
			onload(response) {
				JSG.mapInfo = JSON.parse(response);
			},
			onerror() {
				JSG.debug.logError(`Failed to load map infos`);
			}
		});
		xhr.send();

		// register for command stack changes to update controls and layout app...
		NotificationCenter.getInstance().register(
			this,
			CommandStack.STACK_CHANGED_NOTIFICATION,
			'onCommandStackChanged'
		);
		NotificationCenter.getInstance().register(this, JSG.GRAPH_INVALID_NOTIFICATION, 'redraw');
		NotificationCenter.getInstance().register(this, 'JSON_CONTENT_GET', 'getJSONCellContent');
	}

	createEditor(canvas) {
		this._graphEditor = new GraphEditor(canvas);
		this._canvas = canvas;

		const graph = new Graph();
		this._graphEditor.setGraph(graph);

		// config graph as needed for machine app
		const viewer = this._graphEditor.getGraphViewer();

		this._graphEditor.setDisplayMode(GraphSettings.DisplayMode.ENDLESS);
		this._graphEditor.setScrollPosition(0, 0);
		viewer.getScrollPanel().getViewPanel().setBoundsMargin(0);
		viewer.getScrollPanel().setScrollBarsMode(JSG.ScrollBarMode.HIDDEN);
		this._graphEditor.setZoom(1);

		const defInteraction = this._graphEditor.getGraphViewer().getDefaultInteraction();

		defInteraction.removeAllActivators();
		defInteraction.addActivator(TooltipActivator.KEY, new TooltipActivator());
		defInteraction.addActivator(ButtonActivator.KEY, new ButtonActivator());
		defInteraction.addActivator(CaptionActivator.KEY, new CaptionActivator());
		defInteraction.addActivator(ViewActivator.KEY, new ViewActivator());
		defInteraction.addActivator(ResizeActivator.KEY, new ResizeActivator());
		defInteraction.addActivator(ReshapeActivator.KEY, new ReshapeActivator());
		defInteraction.addActivator(RotateActivator.KEY, new RotateActivator());
		defInteraction.addActivator(SheetGraphItemEventActivator.KEY, new SheetGraphItemEventActivator());
		defInteraction.addActivator(SheetPlotActivator.KEY, new SheetPlotActivator());
		defInteraction.addActivator(SheetActivator.KEY, new SheetActivator());
		defInteraction.addActivator(LayoutNodeActivator.KEY, new LayoutNodeActivator());
		defInteraction.addActivator(EditTextActivator.KEY, new EditTextActivator());
		defInteraction.addActivator(MoveActivator.KEY, new MoveActivator());
		defInteraction.addActivator(MarqueeActivator.KEY, new MarqueeActivator());
		defInteraction.addActivator(PinchActivator.KEY, new PinchActivator());
		defInteraction.addActivator(PanActivator.KEY, new PanActivator());
		defInteraction.addActivator(SplitterActivator.KEY, new SplitterActivator());
		defInteraction.addActivator(TreeActivator.KEY, new TreeActivator());
		defInteraction.addActivator(ImageDropActivator.KEY, new ImageDropActivator());
		defInteraction.addActivator(LinkActivator.KEY, new LinkActivator());

		// create new context only if it was not set before to keep already added functions
		if (!JSG.FormulaParser.context.isSheetParserContext) {
			JSG.FormulaParser.context = new SheetParserContext();
		}

		const menuHandler = this._graphEditor.getItemMenuHandler();
		menuHandler.setMenuProvider(new JSG.DefaultMenuProvider());

		return this._graphEditor;
	}

	getJSONCellContent(notification) {
		const { path, reference, streamsheetId, json: jsn } = notification.object;
		if (path) {
			if (jsn) {
				setJSONContent(path, jsn);
			} else {
				jsonUtils
					.request(this.getMachineId(), streamsheetId, reference, jsn)
					.then((json) => setJSONContent(path, json))
					.catch((err) => JSG.debug.logError(`Failed to get json for cell ${reference}: ${err.message}`));
			}
		}
	}

	prepareTourStep(step) {
		const graph = this.graph;
		const container = graph.streamSheetsContainer;
		const sheetItem = container.leftSheetContainer.streamSheet;
		const viewer = this.getGraphViewer();
		let sheet;
		let shapeName;
		let range;

		if (step.range) {
			range = JSG.CellRange.parse(step.range, sheetItem.model);
			if (!range) {
				return;
			}
			range.shiftFromSheet();
			sheet = range.sheet;
		} else if (step.shape) {
			const cutIndex = step.shape.indexOf('!');
			if (cutIndex === -1) {
				return;
			}
			const name = step.shape.substring(0, cutIndex);
			sheet = JSG.SheetModel.findSheetModel(name);
			shapeName = step.shape.substring(cutIndex + 1);
		} else {
			graph.tourHighlights = undefined;
			return;
		}

		// activate sheet
		const controller = viewer.getRootController().getControllerByModelId(sheetItem.getId());
		const view = controller.getView();
		const index = graph.getSheetIndex(sheet);
		let rect;
		graph.streamSheetsContainer.leftSheetContainer.setActiveSheetIndex(index, viewer);

		if (range) {
			graph.tourHighlights = [sheetItem.getCellRect(range)];
			const viewRange = range.copy();
			viewRange._y2 += 5;
			view.showRange(viewRange);
			rect = view.getRangeRect(range);
		} else {
			const shapeItem = graph.streamSheetsContainer.leftSheetContainer.getItemByName(shapeName);
			if (!shapeItem) {
				return;
			}
			rect = shapeItem.getTranslatedBoundingBox(view.getItem().getCells()).getBoundingRectangle();
			graph.tourHighlights = [rect];
			view.showRect(view.getItem(), rect.copy());
			rect = shapeItem.getTranslatedBoundingBox(view.getItem()).getBoundingRectangle();
		}

		this.redraw();

		const cellPoint = view.translateFromSheet(new JSG.Point(rect.x, rect.y + rect.height), viewer);
		const cs = this._graphEditor.getCoordinateSystem();
		const canvas = this.getCanvas();
		const canvasRect = canvas.getBoundingClientRect();

		step.position = [
			cs.logToDeviceX(cellPoint.x, false) + canvasRect.left,
			cs.logToDeviceY(cellPoint.y + 600, false) + canvasRect.top
		];
	}

	setMachineLanguage(locale) {
		locale = locale || store.getState().machine.locale;

		NumberFormatter.setCulture(locale);
		JSG.setParserLocale(locale);

		// const sheetView = this.getActiveSheetView();
		// if (sheetView === undefined) {
		// 	return;
		// }
		// TODO reavtivate
		// sheetView.notify();
	}

	getCanvas() {
		return this._canvas;
	}

	getGraph() {
		return this._graphEditor ? this._graphEditor.getGraph() : undefined;
	}

	getMachineId() {
		const machine = this.machineContainer;
		return machine && machine.machineId;
	}

	get graph() {
		return this._graphEditor ? this._graphEditor.getGraph() : undefined;
	}

	get interactionHandler() {
		return this._graphEditor ? this._graphEditor.getInteractionHandler() : undefined;
	}

	get machineContainer() {
		const graph = this.graph;
		if (!graph) {
			return undefined;
		}
		return graph.machineContainer;
	}

	setUILanguage(locale) {
		JSG.setLocale(locale, () => this.redraw());
	}

	loadGraph(machine) {
		const graph = this.getGraph();

		JSG.setDrawingDisabled(true);
		// TODO: revise
		graph.getSettings().getSnapStep = () => this._graphEditor.getCoordinateSystem().deviceToLogX(1);

		this._graphEditor.clear();
		this._graphEditor.setInteractionHandler(new AppInteractionHandler(this._graphEditor.getGraphViewer(), graph));
		this._graphEditor.setDisplayMode(GraphSettings.DisplayMode.ENDLESS);
		this._graphEditor.setScrollPosition(0, 0);
		this._graphEditor.getGraphViewer().getScrollPanel().getViewPanel().setBoundsMargin(0);

		graph.init();
		graph.machineContainer.machineId = machine.id;

		this.loadMachine(machine);
		this.updateMachineState(machine.state);
		this.updateDimensions();

		JSG.setDrawingDisabled(false);
		this.redraw();
	}

	createStreamSheet(sheetdef) {
		this.getGraph().streamSheetsContainer.createStreamSheet(sheetdef);
	}

	deleteStreamSheet(streamsheetId) {
		this.getGraph().streamSheetsContainer.deleteStreamSheet(streamsheetId);
	}

	undo() {
		this.getGraphEditor().getInteractionHandler().undo();
		this.redraw();

		// to update toolbar
		const sheetView = this.getActiveSheetView();
		if (sheetView !== undefined) {
			sheetView.notify();
		}
	}

	redo() {
		// const command = this.commandStack.redo();
		this.getGraphEditor().getInteractionHandler().redo(); // command)
		this.redraw();

		// to update toolbar
		const sheetView = this.getActiveSheetView();
		if (sheetView !== undefined) {
			sheetView.notify();
		}
	}

	setUserSelectionId(user) {
		const graph = this.graph;
		if (!graph) {
			return;
		}
		const sheetsContainer = graph.streamSheetsContainer;
		sheetsContainer.enumerateStreamSheetContainers((stscontainer) => {
			const streamsheet = stscontainer.getStreamSheet();
			const sessionId = sessionStorage.getItem('sessionId');
			const selectionId = JSG.StreamSheet.createSelectionId(user, sessionId);
			if (selectionId) streamsheet.setSelectionId(selectionId);

		});
	}

	removeSelection(id) {
		const graph = this.graph;
		if (graph) {
			if (id) {
				const sheetsContainer = graph.streamSheetsContainer;
				sheetsContainer.enumerateStreamSheetContainers((stscontainer) => {
					const streamsheet = stscontainer.getStreamSheet();
					streamsheet.removeSelection(id);
				});
			} else {
				graph.sheetSelection.clear();
			}
		}
	}

	moveSheet(fromIndex, toIndex) {
		const container = this.getGraph().streamSheetsContainer;
		const updateCustomInfosCmd = new UpdateCustomInfosCommand(
			container,
			new MoveSheetCommand(container, fromIndex, toIndex),
			getCustomInfos(sheetOrderInfo())
			// getCustomInfos(sheetOrderInfo(), activeSheetsInfo())
		);
		updateCustomInfosCmd.isVolatile = true;
		this.synchronizedExecute(updateCustomInfosCmd);
	}

	showSheet(sheet, visible = true) {
		const updateCustomInfosCmd = new UpdateCustomInfosCommand(
			sheet,
			new ShowSheetCommand(sheet, visible),
			getCustomInfos(visibleInfo())
		);
		updateCustomInfosCmd.isVolatile = true;
		this.synchronizedExecute(updateCustomInfosCmd);
	}

	getActiveSheetView() {
		return this._graphEditor && this._graphEditor.getGraphViewer()
			? this._graphEditor.getGraphViewer().getGraphView().activeView
			: undefined;
	}
	getActiveSheetModel() {
		const view = this.getActiveSheetView();
		return view ? view.getItem().model : undefined;
	}

	getStreamSheet(streamsheetId) {
		const processSheetContainer = this.getStreamSheetContainer(streamsheetId);
		return processSheetContainer ? processSheetContainer.getStreamSheet() : null;
	}
	getStreamSheetByName(name) {
		const graph = this.getGraph();
		const container = graph && graph.streamSheetsContainer;
		if (container) {
			const streamsheets = container.streamSheetsModel || [];
			return streamsheets.find((model) => model.properties.name === name);
		}
		return undefined;
	}

	handleMachineStep(outbox) {
		this.updateOutbox(outbox);
		this.updateUI();
	}

	handleStreamSheetStep(streamsheetId, currentLoopPath, cells, namedCells, shapes, outbox, stats, inbox, properties) {
		const sheetsContainer = this.getGraph().streamSheetsContainer;
		const sheet = sheetsContainer.find(streamsheetId);
		if (!sheet) {
			return;
		}
		sheet.updateSheet(cells, shapes, namedCells, properties);
		sheet.updateInbox(inbox);
		sheet.properties.loop.currentPath = currentLoopPath;
		sheet.updateStats(stats);
		sheetsContainer.updateTrees(streamsheetId, sheet.properties);
		sheetsContainer.updateStats(streamsheetId, stats);
		sheetsContainer.updateInboxContainers(streamsheetId, sheet.properties.inbox, sheet.properties.loop);
		sheetsContainer.updateShapes(streamsheetId, shapes);
	}

	updateUI() {
		JSG.NotificationCenter.getInstance().send(
			new JSG.Notification(JSG.COMMAND_EXECUTED_NOTIFICATION, { command: this })
		);
		this.sendNotification('streamsheet_settings_notification');


		if (!this.graph || this.graph.showViewMode) {
			return;
		}

		this.updateInfoView();
		this.updateEditBar();
	}

	updateEditBar() {
		if (JSG.CellEditor.getActiveCellEditor() || this.getGraphViewer().getSelection().length) {
			return;
		}

		const view = this.getActiveSheetView();
		if (!view) {
			return;
		}
		const item = view.getItem();
		const activeCell = item.getOwnSelection().getActiveCell();
		const formula = document.getElementById('editbarformula');
		const info = document.getElementById('editbarreference');
		if (!formula) {
			return;
		}

		if (activeCell === undefined) {
			formula.innerHTML = '';
			info.innerHTML = '';
			return;
		}

		formula.innerHTML = view ? view.getEditString(activeCell) : '';
		// info.innerHTML = item.getOwnSelection().refToString();
	}

	updateInfoView() {
		const graph = this.getGraph();
		const info = graph && graph.infoView;
		if (info) {
			CellInfoView.of(info.type, info.viewer, info.view, info.options).showInfo(info.cell, info.target);
		}
	}

	handleCommandResponse(/* response */) {}

	updateStats(streamsheetId, stats) {
		const processSheet = this.getStreamSheet(streamsheetId);
		if (processSheet) {
			processSheet.getStreamSheetContainer().sheetSteps = stats.steps;
		}
	}

	updateCells(streamsheetId, data, shapes, namedCells) {
		const sheet = this.getStreamSheet(streamsheetId);
		if (sheet) {
			return new SetSheetCellsCommand(sheet, data, shapes, namedCells);
		}
		return null;
	}

	rescaleCanvas(canvas) {
		const ctx = canvas.getContext('2d');
		const devicePixelRatio = window.devicePixelRatio || 1;
		const backingStoreRatio =
			ctx.webkitBackingStorePixelRatio ||
			ctx.mozBackingStorePixelRatio ||
			ctx.msBackingStorePixelRatio ||
			ctx.oBackingStorePixelRatio ||
			ctx.backingStorePixelRatio ||
			1;
		const ratio = devicePixelRatio / backingStoreRatio;

		this._graphEditor.getCoordinateSystem().setDeviceRatio(ratio);
		JSG.graphics.getCoordinateSystem().setDeviceRatio(ratio);
		canvas.width = canvas.clientWidth * ratio;
		canvas.height = canvas.clientHeight * ratio;
	}

	updateCanvas(settings, showViewMode) {
		const canvas = document.getElementById('canvas');
		const graph = this.graph;
		if (!canvas || !graph || !settings) {
			return
		}

		graph.viewSettings = settings;
		graph.showViewMode = showViewMode;

		if (canvas.height !== canvas.clientHeight || canvas.width !== canvas.clientWidth) {
			this.updateDimensions();
		}
		NotificationCenter.getInstance().send(new Notification(NotificationCenter.TAB_NOTIFICATION, this));
	}

	updateDimensions() {
		const canvas = document.getElementById('canvas');
		if (!canvas) {
			return;
		}

		this.rescaleCanvas(canvas);

		const cs = this._graphEditor.getCoordinateSystem();
		const width = cs.deviceToLogX(canvas.width) / cs.getDeviceRatio();
		const height = cs.deviceToLogY(canvas.height) / cs.getDeviceRatio();

		this.getGraph().arrange(width, height);

		this._graphEditor.resizeContent(canvas.width, canvas.height);
	}

	redraw() {
		if (this.getGraphEditor() && this.getGraphEditor().getGraph()) {
			this.getGraphEditor().invalidate();
		}
	}

	getGraphEditor() {
		return this._graphEditor;
	}

	getGraphViewer() {
		return this._graphEditor ? this._graphEditor.getGraphViewer() : undefined;
	}

	synchronizedExecute(command) {
		this.getGraphEditor().getInteractionHandler().execute(command);
	}

	execute(command, repaint) {
		this.getGraphEditor().getInteractionHandler().execute(command);
		if (repaint) {
			this.redraw();
		}
	}

	executeCommands(command, options) {
		if (command && command.type === 'message_add') {
			return this.addInboxMessage(command.container, command.message);
		}

		const editor = this.getGraphEditor();

		const cmd = SheetCommandFactory.createCommand(this.getGraph(), command, editor.getGraphViewer());
		if (cmd) {
			// commands received from graph-service are volatile:
			cmd.isVolatile = true;
			if (options && options.undo) {
				editor.getInteractionHandler().undo(cmd);
			} else if (options && options.redo) {
				editor.getInteractionHandler().redo(cmd);
			} else {
				editor.getInteractionHandler().execute(cmd);
			}
		}
		return !!cmd;
	}

	addMessage(messageBox, message, metadata) {
		if (messageBox) {
			const itemsNode = messageBox.getMessageListItems();
			const selectedItem = itemsNode.getTreeItemById(metadata.id);
			if (selectedItem === undefined) {
				this.addNewMessage(messageBox, message, metadata);
			} else {
				this.updateMessage(messageBox, message, metadata);
			}
		}
		return !!messageBox;
	}

	// eslint-disable-next-line
	updateMessage(messageBox, message, metadata, execute = true) {
		const itemsNode = messageBox.getMessageListItems();
		const selectedItem = itemsNode.getTreeItemById(metadata.id);
		if (selectedItem !== undefined) {
			const item = this.getMessageTreeItem(message, metadata, messageBox.getType().getValue());
			const command = new UpdateTreeItemCommand(itemsNode, selectedItem.level, item);
			command.isVolatile = true;

			if (execute) {
				this.execute(command);
				NotificationCenter.getInstance().send(
					new Notification(TreeItemsNode.SELECTION_CHANGED_NOTIFICATION, itemsNode)
				);
			} else {
				return command;
			}
		}
	}

	createUpdateMessageCommand(messageBox, message, metadata) {
		return this.updateMessage(messageBox, message, metadata, false);
	}

	getMessageTreeItem(message, metadata, target) {
		return TreeItem.fromMessage(message, metadata, target);
	}

	// eslint-disable-next-line
	addNewMessage(messageBox, message, metadata, execute = true) {
		const itemsNode = messageBox.getMessageListItems();
		metadata = metadata || {};

		const item = this.getMessageTreeItem(message, metadata, messageBox.getType().getValue());

		const command = new AddTreeItemCommand(itemsNode, -1, item);
		command.isVolatile = true;

		// TODO value: message.time,
		if (execute) {
			this.execute(command, false);
		} else {
			return command;
		}
	}


	clearMessageBox(messageBox) {
		if (messageBox) {
			const itemsNode = messageBox.getMessageListItems();
			const command = new DeleteTreeItemCommand(itemsNode, -1);
			command._keepFeedback = true;
			command.isVolatile = true;
			this.execute(command);
		}
	}
	createAddNewMessageCommand(messageBox, message, metadata) {
		return this.addNewMessage(messageBox, message, metadata, false);
	}
	clearInbox(streamsheetId) {
		this.clearMessageBox(this.getInbox(streamsheetId));
	}
	clearOutbox() {
		this.clearMessageBox(this.getOutbox());
	}

	removeMessage(messageBox, message, metadata) {
		if (messageBox) {
			// messageBox.clearTreeItems();
			const itemsNode = messageBox.getMessageListItems();
			const selectedItem = itemsNode.getTreeItemById(metadata.id);
			if (selectedItem !== undefined) {
				const command = new DeleteTreeItemCommand(itemsNode, selectedItem.level);
				command.isVolatile = true;
				this.execute(command);
			}
		}
	}

	selectMessage(messageBox, messageId, markAsDisabled) {
		if (messageBox) {
			const itemsNode = messageBox.getMessageListItems();
			if (messageId) {
				const selectedItem = itemsNode.getTreeItemById(messageId);
				if (selectedItem !== undefined) {
					const command = new SetSelectionCommand(itemsNode, 'global', selectedItem.id);
					this.execute(command);
					if (markAsDisabled) {
						const treeItemCommand = new SetTreeItemDisabledCommand(
							itemsNode,
							selectedItem.level,
							markAsDisabled
						);
						treeItemCommand.isVolatile = true;
						treeItemCommand._keepFeedback = true;
						this.execute(treeItemCommand);
					}
				}
			} else {
				// id of item to disable ???
				// const command = new SetTreeItemDisabledCommand(itemsNode, selectedItem.level, true);
				const command = new RemoveSelectionCommand(itemsNode, 'global');
				this.execute(command);
			}
		}
	}

	updateMachineNamedCells(namedCells) {
		// const machineGraph = this.getGraph();
		JSG.sheetNames.clear();
		Object.entries(namedCells).forEach(([name, value]) => {
			const sheetName = JSG.sheetNames.getOrCreate(name);
			if (sheetName) {
				const expr = new Expression(value.value, value.formula);
				sheetName.setExpression(expr);
				sheetName.setValue(value.value);
				// sheetName.evaluate();
			}
		});
	}

	updateMachineState(newstate) {
		const graph = this.graph;
		if (graph) {
			graph.machineContainer.machineState = newstate;
			if (newstate === 'running') {
				graph.streamSheetsContainer.leftSheetContainer.getInboxContainer().resetViewports();
				graph.streamSheetsContainer.rightSheetContainer.getInboxContainer().resetViewports();
				const outbox = this.getOutbox();
				if (outbox) {
					outbox.resetViewports();
					const itemsNode = outbox.getMessageListItems();
					this.execute(new RemoveSelectionCommand(itemsNode, 'global'));
				}
			} else {
				this.redraw();
			}
		}
	}

	updateMachineName(name) {
		const graph = this.graph;
		if (graph) {
			graph.machineContainer.name = name;
		}
	}

	updateOutbox(outbox) {
		let id;
		const graph = this.graph;
		if (!graph) {
			return;
		}
		const outboxContainer = this.getOutbox();
		if (!outboxContainer) {
			return;
		}
		const selection = outboxContainer.getMessageListItems().getSelectedItem();
		const tooltip = intl.formatMessage({ id: 'MessageBox.messageCount' }, { messageCount: outbox.totalSize });
		outboxContainer._outboxCaption.setTooltip(tooltip);
		if (selection) {
			id = selection.id;
		}
		this.clearOutbox();
		this.addBoxMessages(outboxContainer, outbox.messages);
		if (id) {
			this.selectMessage(outboxContainer, id, false);
		}
	}

	updateInboxTooltip(streamsheetId, totalSize) {
		const processSheetContainer = this.getStreamSheetContainer(streamsheetId);
		if (processSheetContainer) {
			// const tip = intl.formatMessage({ id: 'MessageBox.messageCount' }, {messageCount: totalSize});
			processSheetContainer.properties.messageCount = totalSize === undefined ? 0 : totalSize;
		}
	}

	addBoxMessages(messageBox, messages = []) {
		const commands = messages.map((message) => {
			const metadata = message.Metadata;
			const itemsNode = messageBox.getMessageListItems();
			const selectedItem = itemsNode.getTreeItemById(metadata.id);
			if (selectedItem === undefined) {
				return this.createAddNewMessageCommand(messageBox, message, metadata);
			}
			return this.createUpdateMessageCommand(messageBox, message, metadata);
		});
		const compoundCommand = new CompoundCommand();
		commands.forEach((command) => compoundCommand.add(command));
		compoundCommand.execute();
		// compoundCommand.sendNotification();
	}

	loadStreamSheets(streamsheets) {
		const viewer = this.getGraphViewer();
		const graph = this.graph;

		graph.streamSheetsContainer.readStreamSheets(streamsheets, viewer);
	}

	loadMachine({ streamsheets, outbox, namedCells, settings, custom, name }) {
		this.updateOutbox(outbox);
		this.updateMachineNamedCells(namedCells);
		this.updateMachineSettings(settings, name);
		this.loadStreamSheets(streamsheets);
		this.updateMachineCustomInfo(custom);

		// default tab selection after load:
		const viewer = this.getGraphViewer();
		const graph = this.graph;

		const container = graph.streamSheetsContainer;
		container.modelToStreamSheetItem(container.leftSheetContainer, 0, viewer, true);
		container.modelToStreamSheetItem(container.rightSheetContainer, 0, viewer, true);

		const sheetItem = graph.streamSheetsContainer.leftSheetContainer.streamSheet;
		const controller = viewer.getGraphController().getControllerByModelId(sheetItem.getId());
		if (!controller) {
			return;
		}
		viewer.getGraphView().setFocus(controller);
		const view = controller.getView();
		view.activateLayerSelection();
	}


	updateStreamSheetCustomInfo(streamsheetId, custom = {}) {
		const { jsg } = custom;
		if (jsg) {
			// SSF: handle missing values
			const { inboxcontainer, type, visible /*, maximized */ } = jsg;

			const sheetsContainer = this.getGraph().streamSheetsContainer;
			const sheet = sheetsContainer.find(streamsheetId);
			if (sheet) {
				if (inboxcontainer) {
					// sheet.properties.inbox.split = inboxcontainer.split;
					// sheet.properties.inbox.width = inboxcontainer.width;
					sheetsContainer.updateInboxContainers(streamsheetId, inboxcontainer);
				}
				if (type) {
					sheet.properties.sheetType = type;
				}
				if (visible != null) {
					sheet.isVisible = visible;
				}
			}
		}
	}

	updateMachineCustomInfo(custom = {}) {
		const { jsg = {} } = custom;
		const { outboxcontainer, sheetorder } = jsg;
		if (sheetorder) this.updateSheetOrder(sheetorder);
		if (outboxcontainer) updateMessageContainer(this.getOutbox(), outboxcontainer);
	}

	updateMachineSettings(settings = {}, name) {
		const container = this.getGraph().machineContainer;
		const { showOutbox, description, /* maximizeSheet, */ locale, defaultInterval, isOPCUA } = settings;
		if (locale != null) this.setMachineLanguage(locale);
		if (showOutbox != null) container.outboxVisible = !!showOutbox;
		if (description != null) container.description = description;
		if (name != null) container.name = name;
		if (isOPCUA != null) container.opcua = isOPCUA;
		if (defaultInterval != null) container.defaultInterval = defaultInterval;
	}

	updateSheetOrder(order = []) {
		const container = this.getGraph().streamSheetsContainer;
		container.setStreamSheetsOrder(order);
	}

	addInboxMessage(streamsheetId, message, totalSize) {
		if (message) this.addMessage(this.getInbox(streamsheetId), message, message.Metadata);
		// const res = this.addMessage(this.getInbox(streamsheetId), message, message.Metadata);
		// return res;
		this.updateInboxTooltip(streamsheetId, totalSize);
	}

	removeInboxMessage(streamsheetId, message, totalSize) {
		this.removeMessage(this.getInbox(streamsheetId), message, message.Metadata);
		this.updateInboxTooltip(streamsheetId, totalSize);
	}

	removeOutboxMessage(message) {
		this.removeMessage(this.getOutbox(), message, message.Metadata);
	}

	selectInboxMessage(streamsheetId, messageId, markAsDisabled) {
		this.setCurrentSelectedMessage(streamsheetId, messageId);
		return this.selectMessage(this.getInbox(streamsheetId), messageId, markAsDisabled);
	}

	setCurrentSelectedMessage(streamsheetId, messageId) {
		const processSheet = this.getStreamSheet(streamsheetId);
		if (processSheet) {
			this.getGraph().context.currentSelectedInboxMessage = messageId;
		}
	}

	getInbox(streamsheetId) {
		const processSheetContainer = this.getStreamSheetContainer(streamsheetId);
		if (!processSheetContainer) {
			console.error(`No process sheet container found for streamsheet '${streamsheetId}'.`);
		}
		return processSheetContainer ? processSheetContainer.getInboxContainer() : null;
	}

	getStreamSheetContainer(streamsheetId) {
		const graph = this.getGraph();
		return graph ? graph.getStreamSheetContainerById(streamsheetId) : undefined;
	}

	getOutbox() {
		const graph = this.getGraph();
		return graph ? graph.outboxContainer : undefined;
	}

	setDrawingDisabled(state) {
		JSG.setDrawingDisabled(state);
		if (state === false) {
			this.redraw();
		}
	}

	getZoom() {
		if (this.getGraphEditor() && this.getGraphEditor().getGraph()) {
			return this.getGraphEditor().getZoom();
		}

		return 1;
	}

	onCommandStackChanged(/* notification */) {
		// layout app:
		const container = this.getGraph().machineContainer;
		if (container) {
			container.layout();
			container.setRefreshNeeded(true);
			this.redraw();
		}
	}

	sendNotification(type, data) {
		JSG.NotificationCenter.getInstance().send(new JSG.Notification(type, data));
	}
}

export const graphManager = new GraphManager('/lib');
